/**
 * Supabase клиент для frontend
 * Автор: Cursor Generator
 * Описание: Инициализация Supabase клиента для авторизации
 */

import { createClient } from '@supabase/supabase-js';

// Получаем переменные окружения
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Проверяем наличие переменных окружения
if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('⚠️ Supabase credentials не найдены. App будет работать в демо режиме.');
  console.log('Environment check:', {
    VITE_SUPABASE_URL: supabaseUrl ? 'Found' : 'Missing',
    VITE_SUPABASE_ANON_KEY: supabaseAnonKey ? 'Found' : 'Missing'
  });
}

// Создаём клиент Supabase с настройками
export const supabase = supabaseUrl && supabaseAnonKey 
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true
      },
      db: {
        schema: 'public'
      },
      global: {
        headers: {
          'X-Client-Info': 'aimarkethub-client'
        }
      }
    })
  : null;

// Вспомогательные функции для работы с авторизацией
export const authHelpers = {
  /**
   * Получает текущую сессию
   */
  async getSession() {
    if (!supabase) return null;
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      console.error('[Supabase Auth] Ошибка получения сессии:', error);
      return null;
    }
    return session;
  },

  /**
   * Получает текущего пользователя
   */
  async getUser() {
    if (!supabase) return null;
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) {
      console.error('[Supabase Auth] Ошибка получения пользователя:', error);
      return null;
    }
    return user;
  },

  /**
   * Регистрация нового пользователя
   */
  async signUp(email, password, metadata = {}) {
    if (!supabase) {
      throw new Error('Supabase не настроен');
    }
    const { redirect_to, ...userMetadata } = metadata;
    const result = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userMetadata,
        emailRedirectTo: redirect_to || 'https://aimarkethub.ru/auth/callback?type=signup'
      }
    });
    
    // Возвращаем полный результат, включая error
    if (result.error) {
      return { error: result.error, data: result.data };
    }
    
    return { data: result.data, error: null };
  },

  /**
   * Вход пользователя
   */
  async signIn(email, password) {
    if (!supabase) {
      throw new Error('Supabase не настроен');
    }
    const result = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    // Возвращаем полный результат, включая error
    if (result.error) {
      return { error: result.error, data: result.data };
    }
    
    return { data: result.data, error: null };
  },

  /**
   * Выход пользователя
   */
  async signOut() {
    if (!supabase) {
      throw new Error('Supabase не настроен');
    }
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  /**
   * Получает access token для отправки в заголовках API
   */
  async getAccessToken() {
    const session = await this.getSession();
    return session?.access_token || null;
  },

  /**
   * Сброс пароля (отправка email для восстановления)
   */
  async resetPassword(email, redirectTo = 'https://aimarkethub.ru/auth/callback') {
    if (!supabase) {
      throw new Error('Supabase не настроен');
    }
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectTo
    });
    if (error) throw error;
    return data;
  }
};

// Экспортируем по умолчанию для обратной совместимости
export default supabase;

